import { type ReactElement, useCallback } from 'react';

import { HelpCircleIcon, XCircleIcon } from '@/common/assets';
import { useNewTranslations } from '@/common/translations';
import type { TranslationKey } from '@/common/types';

import type {
  AssistsPlayerData,
  CardsPlayerData,
  GoalsPlayerData,
  InjuriesPlayerData,
  PlayersTabResponse,
  ShotsPlayerData,
  TeamInfoData,
} from '../../types';
import { PlayerStatsOptionValues, positionTranslationMap } from '../../constants';
import type { PlayerData } from '../../components/PlayersStats/PlayersStats.types';
import type { PlayerStatsOption } from '../../types';
import { getPlayerFlagSrc } from '../../utils/getPlayerFlagSrc';

import sortGoalPlayersData from './sortGoalPlayersData';
import sortAssistPlayersData from './sortAssistPlayersData';
import sortShotPlayersData from './sortShotPlayersData';
import sortCardPlayersData from './sortCardPlayersData';
import sortInjuredPlayersData from './sortInjuredPlayersData';

const injuredInformationMap: Record<
  string,
  { translationKey: TranslationKey; icon: ReactElement }
> = {
  missing: {
    translationKey: 'PreMatchStats.Players.Missing',
    icon: <XCircleIcon className="shrink-0 size-[16px] text-error dark:text-error090" />,
  },
  doubtful: {
    translationKey: 'PreMatchStats.Players.Uncertain',
    icon: <HelpCircleIcon className="shrink-0 size-[16px] text-attention dark:text-attention090" />,
  },
};

interface UseMapPlayersDataParams {
  homeJerseySrc: string;
  awayJerseySrc: string;
  teams: TeamInfoData;
}

const useMapPlayersData = ({ homeJerseySrc, awayJerseySrc, teams }: UseMapPlayersDataParams) => {
  const translate = useNewTranslations();

  const mapGoalsPlayer = useCallback(
    ({ isHome }: { isHome: boolean }) =>
      ({ name, position, flag, goals }: GoalsPlayerData): PlayerData => ({
        name,
        position: position && translate(positionTranslationMap[position.toLocaleLowerCase()]),
        jerseySrc: isHome ? homeJerseySrc : awayJerseySrc,
        flagSrc: getPlayerFlagSrc(isHome, teams, flag),
        isHome,
        goals,
      }),
    [translate, homeJerseySrc, awayJerseySrc, teams],
  );

  const mapAssistsPlayer = useCallback(
    ({ isHome }: { isHome: boolean }) =>
      ({ name, position, flag, assists }: AssistsPlayerData): PlayerData => ({
        name,
        position: position && translate(positionTranslationMap[position.toLocaleLowerCase()]),
        jerseySrc: isHome ? homeJerseySrc : awayJerseySrc,
        flagSrc: getPlayerFlagSrc(isHome, teams, flag),
        isHome,
        assists,
      }),
    [translate, homeJerseySrc, awayJerseySrc, teams],
  );

  const mapShotsPlayer = useCallback(
    ({ isHome }: { isHome: boolean }) =>
      ({ name, position, flag, shotsOnTarget, shotsOffTarget }: ShotsPlayerData): PlayerData => ({
        name,
        position: position && translate(positionTranslationMap[position.toLocaleLowerCase()]),
        jerseySrc: isHome ? homeJerseySrc : awayJerseySrc,
        flagSrc: getPlayerFlagSrc(isHome, teams, flag),
        isHome,
        shotsOnTarget,
        shotsOffTarget,
      }),
    [translate, homeJerseySrc, awayJerseySrc, teams],
  );

  const mapCardsPlayer = useCallback(
    ({ isHome }: { isHome: boolean }) =>
      ({ name, position, flag, yellowCards, redCards }: CardsPlayerData): PlayerData => ({
        name,
        position: position && translate(positionTranslationMap[position.toLocaleLowerCase()]),
        jerseySrc: isHome ? homeJerseySrc : awayJerseySrc,
        flagSrc: getPlayerFlagSrc(isHome, teams, flag),
        isHome,
        yellowCards,
        redCards,
      }),
    [translate, homeJerseySrc, awayJerseySrc, teams],
  );

  const mapInjuredPlayer = useCallback(
    ({ isHome }: { isHome: boolean }) =>
      ({ name, position, flag, status }: InjuriesPlayerData): PlayerData => ({
        name,
        position: position && translate(positionTranslationMap[position.toLocaleLowerCase()]),
        jerseySrc: isHome ? homeJerseySrc : awayJerseySrc,
        flagSrc: getPlayerFlagSrc(isHome, teams, flag),
        isHome,
        injuryStatus: status,
        injury:
          status && translate(injuredInformationMap[status.toLocaleLowerCase()]?.translationKey),
        injuryIcon: status && injuredInformationMap[status.toLocaleLowerCase()]?.icon,
      }),
    [translate, homeJerseySrc, awayJerseySrc, teams],
  );

  const mapAndSortPlayersData = useCallback(
    (players: PlayersTabResponse | null): Record<PlayerStatsOption, PlayerData[]> | null =>
      players && {
        [PlayerStatsOptionValues.GOALS]: [
          ...players.home.goals.map(mapGoalsPlayer({ isHome: true })),
          ...players.away.goals.map(mapGoalsPlayer({ isHome: false })),
        ].sort(sortGoalPlayersData),
        [PlayerStatsOptionValues.ASSISTS]: [
          ...players.home.assists.map(mapAssistsPlayer({ isHome: true })),
          ...players.away.assists.map(mapAssistsPlayer({ isHome: false })),
        ].sort(sortAssistPlayersData),
        [PlayerStatsOptionValues.SHOTS]: [
          ...players.home.shots.map(mapShotsPlayer({ isHome: true })),
          ...players.away.shots.map(mapShotsPlayer({ isHome: false })),
        ].sort(sortShotPlayersData),
        [PlayerStatsOptionValues.CARDS]: [
          ...players.home.cards.map(mapCardsPlayer({ isHome: true })),
          ...players.away.cards.map(mapCardsPlayer({ isHome: false })),
        ].sort(sortCardPlayersData),
        [PlayerStatsOptionValues.INJURED]: [
          ...players.home.injuries.map(mapInjuredPlayer({ isHome: true })),
          ...players.away.injuries.map(mapInjuredPlayer({ isHome: false })),
        ].sort(sortInjuredPlayersData),
      },
    [mapGoalsPlayer, mapAssistsPlayer, mapShotsPlayer, mapCardsPlayer, mapInjuredPlayer],
  );

  return mapAndSortPlayersData;
};

export default useMapPlayersData;
